import { toWallClockTimestamp } from '../../../utils/timezone';
import type { Machine, RentalExtensionAlternative, RentalExtensionSuggestion } from '../types';

export const EXTENSION_MATCH_WINDOW_DAYS = 2;

const MS_PER_DAY = 86400000;

function normalizeCategory(category?: string): string {
  return (category ?? '').trim().toLowerCase();
}

// Two machines only ever match if they'd be delivered to the same address — using one
// machine in place of ordering another only saves a trip if it's already at the right
// place. Machines without an address assigned yet are grouped together under one key
// rather than excluded outright.
function bucketKey(category: string | undefined, addressId: string | null | undefined): string {
  return `${normalizeCategory(category)}::${addressId ?? ''}`;
}

// `matchWindowDays` is signed and controls direction, not just magnitude: a positive value
// shows only non-overlapping matches (0 <= gapDays <= matchWindowDays — the tightest possible
// fit, an exact 0, is included), a negative value shows only strictly overlapping matches
// (matchWindowDays <= gapDays < 0 — the exact-0 seamless match is excluded here, since 0 isn't
// actually an overlap), and 0 shows only an exact seamless match.
function isWithinMatchWindow(gapDays: number, matchWindowDays: number): boolean {
  if (matchWindowDays < 0) return gapDays < 0 && gapDays >= matchWindowDays;
  return gapDays >= 0 && gapDays <= matchWindowDays;
}

// Any machine's end date can anchor a match, but only while it still represents a real
// departure: an unconfirmed pickup keeps the machine eligible indefinitely (it's still
// physically on site until pickup is confirmed), a confirmed one only while its end date
// hasn't passed yet — once confirmed and past, the machine is verifiably gone and can never
// be a real alternative. A to_order machine has no meaningful "departure" of its own yet, but
// it can still anchor a match this way once it's been ordered.
function isEligibleAsEnder(machine: Machine, today: string): boolean {
  if (!machine.category || !machine.end_date) return false;
  const isSecured = machine.status === 'ordered' || machine.status === 'collected';
  if (isSecured) {
    if (machine.pickup_on_end_date && machine.end_date < today) return false;
  }
  return true;
}

// For a single to_order machine (the "anchor" — the order you're trying to avoid placing
// separately), finds every other machine of the same category AND the same delivery address
// whose availability window ends right as the anchor needs to start, within `matchWindowDays`.
// Each to_order machine gets its own suggestion, listing its own direct alternatives — there's
// no attempt to merge separate anchors' alternatives into one combined chain, since that
// produced ambiguous, sometimes-wrong-looking recommendations once one machine's window fully
// contained another's.
//
// Machines are bucketed by category + address and every date+time parsed to a timestamp once
// up front, so this stays O(anchors × same-category-and-address machines) with cheap integer
// arithmetic in the inner loop, rather than O(all anchors × all machines) with a dayjs parse
// on every comparison — the difference matters once a project has hundreds/thousands of
// machines.
export function computeRentalExtensionSuggestions(
  machines: Machine[],
  today: string,
  matchWindowDays: number = EXTENSION_MATCH_WINDOW_DAYS,
  considerOptions: boolean = false,
): RentalExtensionSuggestion[] {
  const anchors = machines.filter((m) => m.status === 'to_order' && !!m.category);
  if (anchors.length === 0) return [];

  const startTimestamps = new Map<string, number | undefined>();
  const endTimestamps = new Map<string, number | undefined>();
  const enderEligible = new Map<string, boolean>();
  const categoryBuckets = new Map<string, Machine[]>();

  for (const m of machines) {
    startTimestamps.set(m.id, toWallClockTimestamp(m.start_date, m.start_time));
    endTimestamps.set(m.id, toWallClockTimestamp(m.end_date, m.end_time));
    enderEligible.set(m.id, isEligibleAsEnder(m, today));
    const key = bucketKey(m.category, m.address_id);
    const bucket = categoryBuckets.get(key);
    if (bucket) bucket.push(m);
    else categoryBuckets.set(key, [m]);
  }

  const suggestions: RentalExtensionSuggestion[] = [];

  for (const anchor of anchors) {
    const bucket = categoryBuckets.get(bucketKey(anchor.category, anchor.address_id)) ?? [];
    const anchorStartTs = startTimestamps.get(anchor.id);
    const alternatives: RentalExtensionAlternative[] = [];

    for (const other of bucket) {
      if (other.id === anchor.id) continue;

      // Always computed — it's shown as plain information regardless of the
      // considerOptions setting. `considerOptions` only controls whether an alternative
      // missing even a single option gets excluded from the results outright, in favor of
      // only ever showing machines that fully match.
      const missingOptions = (anchor.options ?? []).filter(
        (opt) => !(other.options ?? []).includes(opt)
      );
      if (considerOptions && missingOptions.length > 0) continue;

      const otherEndTs = endTimestamps.get(other.id);
      if (anchorStartTs === undefined || otherEndTs === undefined || !enderEligible.get(other.id)) continue;

      const gap = Math.floor((anchorStartTs - otherEndTs) / MS_PER_DAY);
      if (isWithinMatchWindow(gap, matchWindowDays)) {
        alternatives.push({ machine: other, gapDays: gap, missingOptions });
      }
    }

    if (alternatives.length === 0) continue;

    // A single `matchWindowDays` value can never produce a mix of strictly-positive and
    // strictly-negative gaps in one result set (see `isWithinMatchWindow` above) — only 0 can
    // share a bucket with same-signed values, and 0 is already the smallest possible
    // magnitude — so plain ascending-|gap| ordering is sufficient.
    alternatives.sort((a, b) => Math.abs(a.gapDays) - Math.abs(b.gapDays));
    suggestions.push({ anchor, alternatives });
  }

  return suggestions.sort((a, b) => (a.anchor.start_date ?? '').localeCompare(b.anchor.start_date ?? ''));
}
